/**
 * Menu yang DITAMPILKAN — bukan yang DIIZINKAN.
 *
 * Penegakan izin sepenuhnya milik API (`RequirePermission` + guard). Modul ini hanya mencegah
 * pengguna menabrak layar yang pasti menolaknya. Menghapus penyaringan ini tidak memberi
 * seorang pun akses tambahan; ia hanya membuat menu berisi jalan buntu.
 */

/**
 * Kelompok pada halaman Lainnya (item 6, review 12 Sep 2026) — MURNI KATEGORISASI, tidak
 * berhubungan dengan peran. Peran menentukan APAKAH sebuah menu tampil (lewat `anyOf`/`allOf`,
 * sudah ada); kelompok hanya menentukan DI BAWAH JUDUL MANA ia dikelompokkan bila ia berakhir
 * di halaman Lainnya. Menu yang selalu ada di bilah utama (Beranda, Absen, Tugas) sengaja tidak
 * diberi kelompok — ia hanya akan terpakai bila kombinasi izin yang tidak biasa mendorongnya ke
 * luapan, dan saat itu terjadi ia jatuh ke bagian tanpa judul, bukan hilang.
 */
export type NavGroup = 'saya' | 'operasional' | 'pengaturan';

export interface NavItem {
  readonly to: string;
  readonly label: string;
  /** Salah satu saja sudah cukup untuk menampilkan. Kosong = selalu tampil. */
  readonly anyOf: readonly string[];
  /**
   * SELURUHNYA wajib dimiliki. Kosong = tidak membatasi.
   *
   * ADA KARENA `anyOf` tidak dapat menyatakan syarat sebuah layar yang memanggil BEBERAPA
   * endpoint dengan izin berbeda. Dua layar seperti itu ditemukan 23 Agustus 2026 — Dashboard
   * Absensi dan Karyawan — dan keduanya ditawari menu kepada orang yang lalu ditolak API:
   * `GET /api/members` dijaga `workforce.worker.create`, sementara gerbang menunya hanya menuntut
   * `attendance.read` (dashboard) atau `worker.read` (karyawan).
   *
   * Itu persis kegagalan yang komentar di puncak berkas ini bilang ingin dicegah, dan yang uji
   * navigasi sudah menuliskan alasannya: "menampilkannya hanya mengantar pengguna ke penolakan
   * API".
   */
  readonly allOf?: readonly string[];
  readonly group?: NavGroup;
}

export const NAV_ITEMS: readonly NavItem[] = [
  { to: '/', label: 'Beranda', anyOf: [] },
  {
    to: '/attendance/check',
    /*
     * "Absen", bukan "Check In / Out".
     *
     * Dua alasan yang bertemu. Pertama ukuran: pada bilah melayang di layar 390 px tiap item
     * hanya punya ~59 px, sedangkan "Check In / Out" butuh ~78 px — ia sudah terpotong 5 px
     * bahkan sebelum bilahnya melayang, dan memperkecil padding maupun font tidak menutup
     * selisihnya. Kedua bahasa: ia satu-satunya label Inggris di antara Beranda, Riwayat,
     * Koreksi, Tugas, Pengiriman, Menu.
     *
     * "Absen" mencakup masuk DAN pulang dalam pemakaian sehari-hari, dan layar tujuannya sendiri
     * yang menyebut tindakan tepatnya ("Check In" atau "Check Out") sesuai keadaan sesi.
     */
    label: 'Absen',
    anyOf: ['workforce.attendance.check_in', 'workforce.attendance.check_out'],
  },
  {
    to: '/attendance/history',
    label: 'Riwayat',
    anyOf: ['workforce.attendance.read'],
    group: 'saya',
  },
  {
    // Satu menu untuk dua peran: karyawan melihat pengajuannya sendiri, manajer melihat antrean
    // timnya. Yang membedakan adalah scope yang ditegakkan SERVER, bukan dua menu berbeda yang
    // perlahan menyimpang. `correction.read` sudah cukup untuk membuka daftar; tombol keputusan
    // disaring terpisah di halamannya (`decisionAbility`).
    to: '/attendance/corrections',
    label: 'Koreksi',
    anyOf: ['workforce.attendance.correction.read'],
    group: 'saya',
  },
  {
    to: '/tasks',
    label: 'Tugas',
    anyOf: ['workforce.task.read'],
  },
  {
    /*
     * Kelola Karyawan — menghubungkan anggota organisasi dengan profil pekerja.
     *
     * Ditampilkan pada `worker.read` (manajer boleh melihat daftar), bukan `worker.create`:
     * menyembunyikan seluruh menu dari manajer akan membuat pertanyaan "kenapa absensi si A
     * tidak muncul di dashboard" tidak punya tempat untuk dijawab. Tombol tambah dan ubah
     * status disaring terpisah di halamannya.
     */
    to: '/workers',
    label: 'Karyawan',
    anyOf: ['workforce.worker.read'],
    group: 'pengaturan',
  },
  {
    // Alat (installed base) + remote — ADR-0054 Fase 1. Hanya untuk yang bisa membaca alat
    // (teknisi/manajer/admin); `asset.read` sudah cukup membuka daftar.
    to: '/assets',
    label: 'Alat',
    anyOf: ['workforce.asset.read'],
    group: 'operasional',
  },
  {
    /*
     * Halaman Alert (ADR-0055 Fase 3) — kejadian ambang & offline seluruh armada.
     *
     * Memanggil DUA endpoint berizin berbeda: `/alert-events` (`telemetry.read`) dan `/assets`
     * (`asset.read`, untuk menerjemahkan id alat menjadi nama) — jadi `allOf` menuntut keduanya,
     * pola sama dengan Tim & Dashboard Absensi, agar menu tak mengantar ke layar yang lalu ditolak
     * API. Severity dari aturan (`asset.update`) hanya pengayaan opsional — TIDAK disyaratkan,
     * karena halaman tetap berguna tanpanya.
     */
    to: '/alerts',
    label: 'Alert',
    anyOf: ['workforce.telemetry.read'],
    allOf: ['workforce.telemetry.read', 'workforce.asset.read'],
    group: 'operasional',
  },
  {
    /*
     * Pengaturan aturan alert (ADR-0055 Fase 3 slice 4) — kelola ambang telemetri.
     *
     * Seluruh endpoint `/alert-rules` (list/create/update) dijaga `workforce.asset.update` — izin
     * yang sama dengan edit alat & token agen (BUKAN permission baru). Jadi menunya cukup
     * `asset.update`; tak ada endpoint lain yang disyaratkan halaman ini (jenis tugas untuk tiket
     * otomatis dimuat best-effort, bukan prasyarat).
     */
    to: '/alert-rules',
    label: 'Aturan alert',
    anyOf: ['workforce.asset.update'],
    group: 'pengaturan',
  },
  {
    to: '/teams',
    label: 'Tim',
    /*
     * Layar ini memanggil DUA endpoint berizin berbeda: `/api/teams` (`team.read`) dan
     * `/api/members` (`worker.read`, untuk menerjemahkan id menjadi nama). Menyebut salah satu
     * saja mengulang cacat Dashboard Absensi — menu menjanjikan layar yang lalu ditolak API.
     */
    anyOf: ['workforce.team.read'],
    allOf: ['workforce.team.read', 'workforce.worker.read'],
    group: 'pengaturan',
  },
  {
    /*
     * Menu ini hanya untuk yang benar-benar dapat MENGATUR lokasi kerja.
     *
     * `location.read` dimiliki SEMUA peran — ia termasuk referensi baca-saja yang dibutuhkan layar
     * check-in untuk menghitung jarak dan menjelaskan penolakan. Memakainya sebagai syarat menu
     * membuat kurir melihat layar yang isinya hanya daftar yang tidak bisa ia ubah, di aplikasi
     * yang sudah punya sebelas menu.
     *
     * API tetap melayani `read` untuk siapa pun — yang disembunyikan menunya, bukan datanya.
     */
    to: '/attendance-policy',
    label: 'Kebijakan Absensi',
    /*
     * Hanya yang boleh MENGUBAHNYA yang melihat menunya — `policy.update` dipegang WORKFORCE_ADMIN
     * saja. Memakai `policy.read` sebagai syarat akan menampilkan layar berisi setelan yang tidak
     * dapat disentuh sebagian besar orang, di aplikasi yang sudah punya banyak menu.
     */
    anyOf: ['workforce.attendance.policy.update'],
    group: 'pengaturan',
  },
  {
    to: '/task-settings',
    label: 'Setelan Tugas',
    /*
     * Izin MEMBUAT jenis tugas, bukan membacanya. Kurir memakai `task.type.read` untuk melihat
     * jenis tugasnya sendiri — memakainya sebagai syarat menu akan menampilkan layar setelan
     * kepada orang yang tidak dapat mengubah satu pun barisnya.
     */
    anyOf: ['workforce.task.type.create'],
    group: 'pengaturan',
  },
  {
    to: '/work-locations',
    label: 'Lokasi Kerja',
    anyOf: ['workforce.location.create'],
    group: 'pengaturan',
  },
  {
    /*
     * BLD-005 (26 Sep 2026) menampilkannya untuk SEMUA anggota — harga bukan rahasia.
     *
     * DIUBAH keputusan Owner 24 Okt 2026 (#1758): "Paket & Harga hanya untuk Admin". Alasannya
     * bukan kerahasiaan — rutenya tetap terbuka bagi siapa pun yang membuka `/paket` — melainkan
     * kebisingan: kurir, karyawan, dan teknisi tidak pernah memutuskan paket, dan menu yang tidak
     * pernah relevan bagi pemakainya membuat menu yang relevan lebih sulit ditemukan.
     *
     * `role.assign` dipakai sebagai penanda "Admin organisasi" karena hanya WORKFORCE_ADMIN yang
     * memegangnya — orang yang memang mengurus siapa ikut di organisasi ini, dan karenanya paketnya.
     */
    to: '/paket',
    label: 'Paket & Harga',
    anyOf: ['workforce.role.assign'],
    group: 'pengaturan',
  },
  {
    // Menu dispatcher terpisah dari "Tugas": yang pertama daftar milik pelaksana, yang kedua
    // seluruh tugas dalam cakupan. Menggabungkannya memaksa pelaksana lapangan menyaring
    // daftar yang bukan miliknya setiap kali membuka aplikasi.
    to: '/tasks/manage',
    label: 'Kelola Tugas',
    anyOf: ['workforce.task.assign', 'workforce.task.create'],
    group: 'operasional',
  },
  /*
   * "Pengiriman" TIDAK LAGI berdiri sendiri di sini (dulu `to: '/deliveries'`) — PEMBALIKAN
   * ketetapan Owner 25 Agu 2026, dicabut Owner sendiri 12 Sep 2026 (review, item 6): "isinya
   * identik" dengan tab Pengiriman di Laporan (`LaporanPage.tsx` merender `DeliveryBoardPage`
   * yang SAMA), dan dua pintu ke layar yang sama kini dianggap pemborosan slot bilah bawah, bukan
   * kenyamanan. Rutenya (`/deliveries`) TIDAK dihapus — hanya tidak lagi ditawarkan sebagai menu.
   */
  {
    /*
     * SATU pintu untuk seluruh laporan (ketetapan Owner 31 Agu 2026, mengikuti Reports di CRM).
     *
     * Sebelumnya empat menu berdiri sendiri dengan nama yang mirip — Pantauan Absensi, Rekap
     * Bulanan, Pantauan Tugas, Pengiriman — sehingga orang harus menebak mana yang memuat angka
     * yang dicarinya, dan menutup tiga layar untuk membandingkan dua angka.
     *
     * `anyOf` memuat SELURUH izin yang membuka salah satu tab: menu ini muncul bila ada SATU saja
     * laporan yang boleh dilihat, dan halaman di dalamnya yang menyaring tab-nya. Menuntut lebih
     * di sini akan menyembunyikan seluruh Laporan dari orang yang berhak melihat sebagiannya.
     */
    to: '/laporan',
    label: 'Laporan',
    anyOf: ['workforce.attendance.read', 'workforce.task.read', 'workforce.delivery.read'],
    group: 'operasional',
  },
];

/**
 * `workforce.superuser` sengaja diperlakukan sebagai pembuka semua menu, sejalan dengan katalog
 * permission di API — bukan sebagai peran terpisah dengan daftar menu sendiri yang akan menyimpang.
 */
export function visibleNavItems(
  permissions: readonly string[],
  items: readonly NavItem[] = NAV_ITEMS,
): readonly NavItem[] {
  const owned = new Set(permissions);
  const superuser = owned.has('workforce.superuser');
  return items.filter((item) => {
    if (superuser) return true;
    const lolosAny = item.anyOf.length === 0 || item.anyOf.some((p) => owned.has(p));
    /*
     * `allOf` adalah SYARAT TAMBAHAN, bukan pengganti. Keduanya harus lolos.
     *
     * Dipisah begini supaya menambahkannya pada satu menu tidak mengubah arti menu lain: item
     * tanpa `allOf` berperilaku persis seperti sebelum 23 Agu 2026.
     */
    const lolosAll = (item.allOf ?? []).every((p) => owned.has(p));
    return lolosAny && lolosAll;
  });
}

const GROUP_LABEL: Readonly<Record<NavGroup, string>> = {
  saya: 'Saya',
  operasional: 'Operasional',
  pengaturan: 'Pengaturan',
};

const GROUP_URUTAN: readonly NavGroup[] = ['saya', 'operasional', 'pengaturan'];

export interface NavSection {
  /** `null` = bagian tanpa judul — untuk menu yang belum diberi `group` (lihat `NavItem.group`). */
  readonly group: NavGroup | null;
  readonly label: string | null;
  readonly items: readonly NavItem[];
}

/**
 * Mengelompokkan menu untuk halaman Lainnya (item 6, review 12 Sep 2026): **Saya** ·
 * **Operasional** · **Pengaturan**, dalam urutan itu, lalu bagian TANPA JUDUL untuk apa pun yang
 * belum diberi `group` — bukan dibuang, dan bukan dipaksa masuk ke salah satu dari tiga judul
 * yang tidak menyebutnya.
 *
 * Bagian yang hasilnya kosong (tidak ada satu pun menunya yang boleh dilihat peran ini)
 * DIBUANG SELURUHNYA, bukan ditampilkan sebagai judul kosong — pola yang sama dengan
 * `countByStatus` di `delivery-board.ts` sengaja BERBEDA: yang itu papan tetap (kolom nol
 * tetaplah informasi), ini daftar navigasi (judul tanpa isi hanya sampah tampilan).
 */
export function groupedNavItems(items: readonly NavItem[]): readonly NavSection[] {
  const seksi: NavSection[] = [];
  for (const group of GROUP_URUTAN) {
    const isi = items.filter((i) => i.group === group);
    if (isi.length > 0) seksi.push({ group, label: GROUP_LABEL[group], items: isi });
  }
  const tanpaGrup = items.filter((i) => i.group === undefined);
  if (tanpaGrup.length > 0) seksi.push({ group: null, label: null, items: tanpaGrup });
  return seksi;
}

/** Benar bila aktor memiliki setidaknya satu dari izin yang diminta. */
export function hasAnyPermission(
  permissions: readonly string[],
  anyOf: readonly string[],
): boolean {
  if (anyOf.length === 0) return true;
  const owned = new Set(permissions);
  if (owned.has('workforce.superuser')) return true;
  return anyOf.some((p) => owned.has(p));
}
